import React from 'react';
import './EnterpriseArchitecture.scss';
import { Helmet } from 'react-helmet';
import { useInView } from '../hooks/useInView';

import heroVector from '../Assets/Images/enterprisearchitecture/ea-hero-vector.svg';
import heroVectorMobile from '../Assets/Images/enterprisearchitecture/ea-hero-vector-mobile.svg';
import howVectorMobile from '../Assets/Images/enterprisearchitecture/ea-how-vector-mobile.svg';
import glowBg from '../Assets/Images/enterprisearchitecture/ea-glow-bg.png';
import checkOrange from '../Assets/Images/enterprisearchitecture/ea-check-orange.svg';
import checkDark from '../Assets/Images/enterprisearchitecture/ea-check-dark.svg';

const businessOutcomes = [
  'Clear map of how the organization currently operates',
  'Identification of bottlenecks, duplications, and unnecessary costs',
  'Target Operating Model — future organization, processes, and roles',
  "Business capabilities map: today vs. what's required going forward",
  'Transformation roadmap from current to desired state',
];

const techOutcomes = [
  'Full system landscape inventory — applications, databases, integrations',
  'Redundancy assessment — systems that duplicate functionality and can be removed',
  'Target IT architecture blueprint',
  'Integration model — APIs, ESB, cloud services',
  'Technology roadmap — phased plan for new solutions and migrations',
];

function EnterpriseArchitecture() {
  const { ref: heroRef, visible: heroVisible } = useInView(0.05);
  const { ref: problemRef, visible: problemVisible } = useInView(0.1);
  const { ref: statsRef, visible: statsVisible } = useInView(0.1);
  const { ref: howRef, visible: howVisible } = useInView(0.1);
  const { ref: outcomesRef, visible: outcomesVisible } = useInView(0.1);

  return (
    <div className="ea-page">
      <Helmet>
        <title>Artifit - Enterprise Architecture</title>
        <meta
          name="description"
          content="Jointly map what you have, design what you need, and produce a roadmap to get you there — tied to real projects and real constraints."
        />
      </Helmet>

      {/* ── HERO ── */}
      <section className="ea-hero">
        <img src={heroVector} alt="" className="ea-hero-vector ea-hero-vector--desktop" />
        <img src={heroVectorMobile} alt="" className="ea-hero-vector ea-hero-vector--mobile" />
        <div
          className={`ea-hero-content anim-fade-up${heroVisible ? ' is-visible' : ''}`}
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <h1 className="ea-hero-title">
            <span className="ea-orange">Enterprise</span>
            <br />
            Architecture
          </h1>
          <p className="ea-hero-sub">
            The structural backbone<br />of your digital enterprise.
          </p>
          <p className="ea-hero-desc">
            Jointly map what you have, design what you need, and produce a roadmap to get you
            there&nbsp;- tied to real projects and real constraints
          </p>
          <a href="mailto:office@artifit.rs" className="ea-btn-primary">
            Get in touch
          </a>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="ea-problem" id="ea-problem">
        <div
          className={`ea-problem-inner anim-fade-up${problemVisible ? ' is-visible' : ''}`}
          ref={problemRef as React.RefObject<HTMLDivElement>}
        >
          <div className="ea-problem-left">
            <h2 className="ea-problem-h2">
              Most organizations are running on years of accumulated decisions
            </h2>
            <p className="ea-problem-body">
              Systems that overlap. Processes that depend on specific people. A strategy that quietly
              drifted from the technology meant to support it.
            </p>
            <p className="ea-problem-em">
              Enterprise Architecture makes all of that visible and more important&nbsp;- actionable.
            </p>
            <p className="ea-problem-body">
              Whether you&apos;re preparing for a major transformation, rationalizing your application
              landscape, or simply trying to answer &ldquo;what do we actually have?&rdquo;, this is
              where the work starts.
            </p>
          </div>

          <div className="ea-problem-right">
            <p className="ea-key-title">
              <span className="ea-orange">Key points</span> to consider
            </p>
            <ul className="ea-key-list">
              <li>Do you have full visibility into your system landscape and dependencies?</li>
              <li>How fast can new people get up to speed?</li>
              <li>Can your architecture support your strategy&nbsp;— or contradict it?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="ea-stats">
        <div
          className={`ea-stats-inner anim-fade-up${statsVisible ? ' is-visible' : ''}`}
          ref={statsRef as React.RefObject<HTMLDivElement>}
        >
          <div className="ea-stat">
            <p className="ea-stat-num">2-3x</p>
            <p className="ea-stat-label">
              Faster onboarding of<br />new employees with<br />documented architecture
            </p>
          </div>
          <div className="ea-stat-sep" />
          <div className="ea-stat">
            <p className="ea-stat-num">20%</p>
            <p className="ea-stat-label">
              Up to 20% identified redundancies in application and system portfolios
            </p>
          </div>
          <div className="ea-stat-sep" />
          <div className="ea-stat">
            <p className="ea-stat-num">70%</p>
            <p className="ea-stat-label">
              ~&nbsp;70% unlocking path to success of enterprise digital transformations
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW WE WORK + OUTCOMES (shared glow bg) ── */}
      <div className="ea-mid-wrapper">
        <img src={glowBg} alt="" className="ea-outcomes-glow" />

        <section className="ea-how">
          <img src={howVectorMobile} alt="" className="ea-how-vector-mobile" />
          <div
            className={`ea-how-inner anim-fade-up${howVisible ? ' is-visible' : ''}`}
            ref={howRef as React.RefObject<HTMLDivElement>}
          >
            <h2 className="ea-how-title">
              <span className="ea-orange">How</span> we work
            </h2>
            <p className="ea-how-desc">
              We cover the full architecture stack — business and functional architecture, application
              and technology landscape, information flows, and integration design. We model it all in
              Sparx Enterprise Architect, so what you get is living documentation your teams can
              actually use and maintain.
            </p>
            <div className="ea-how-cards">
              <div className="ea-how-card ea-how-card--orange">
                <h3 className="ea-how-card-title">Current State</h3>
                <p className="ea-how-card-body">
                  Business and IT architecture review — capabilities, value flows, application
                  landscape, and information architecture.
                </p>
              </div>
              <div className="ea-how-card ea-how-card--dark">
                <h3 className="ea-how-card-title">To-Be State</h3>
                <p className="ea-how-card-body">
                  Target Operating Model, capability gap analysis, and blueprint of the future IT
                  landscape.
                </p>
              </div>
              <div className="ea-how-card ea-how-card--orange">
                <h3 className="ea-how-card-title">Transition Guidance</h3>
                <p className="ea-how-card-body">
                  Actionable recommendations and phased roadmap with project sequencing and governance
                  guidance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── OUTCOMES ── */}
        <section className="ea-outcomes">
        <div
          className={`ea-outcomes-inner anim-fade-up${outcomesVisible ? ' is-visible' : ''}`}
          ref={outcomesRef as React.RefObject<HTMLDivElement>}
        >
          <div className="ea-outcomes-card">
            <div className="ea-outcomes-header ea-outcomes-header--orange">
              Business Outcomes
            </div>
            <ul className="ea-outcomes-list">
              {businessOutcomes.map((item, i) => (
                <li key={i} className="ea-outcomes-item">
                  <img src={checkOrange} alt="" className="ea-outcomes-check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="ea-outcomes-card">
            <div className="ea-outcomes-header ea-outcomes-header--dark">
              Technology Outcomes
            </div>
            <ul className="ea-outcomes-list">
              {techOutcomes.map((item, i) => (
                <li key={i} className="ea-outcomes-item">
                  <img src={checkDark} alt="" className="ea-outcomes-check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        </section>
      </div>

      {/* ── CTA ── */}
      <section className="ea-cta">
        <p className="ea-cta-text">
          Every strong system starts with a clear blueprint. Let&apos;s draw yours.
        </p>
        <a href="mailto:office@artifit.rs">
          <button className="ea-cta-btn">Get in touch</button>
        </a>
      </section>
    </div>
  );
}

export default EnterpriseArchitecture;
